
import Modal from "components/Modal"
import ScreenLoading from "components/ScreenLoading"
import useConversation from "containers/conversations/useConversation"
import OrganizationsContext from "containers/organizations/organizationsContext"
import { useContext, useState } from "react"
import { useHistory } from "react-router-dom"
import { useSelector } from "react-redux"
import "./Products.scss"
import { IShopifyPromoCode, Product, ProductMode } from "types"
import api from "containers/api"
import { selectToken } from "containers/authentication/authenticationSlice"
import { SHOPIFY_APP_STORE_URL } from "constants/shopify"

interface IProductCardProps {
  product: Product,
  toggle: boolean,
  isShopifyFlow?: boolean
  // The store being subscribed (?shop= URL param) — passed to the backend so the charge
  // lands on that store, since connections are inactive until their subscription activates.
  shopDomain?: string | null
  // Store-switch resubscribe (?switched=true): forwarded so the backend embeds it in the
  // Shopify return URL and the post-purchase page waits for the NEW store's first sync.
  switchedStore?: boolean
  // A promo code entered on the plan page, sent with the Shopify subscribe call.
  promoCode?: string
  // The applied promo code, when it's for the selected billing: shown as the discounted price.
  promo?: IShopifyPromoCode | null
}

const Features = ({product, show}) => {
    const CheckMark = () => <span className="material-icons-outlined" style={{color: "green"}}>check</span>
    
    const XMark = () => <span className="material-icons-outlined" style={{color: "red"}}>close</span>

    return (
        <>
            <div className={`product-features ${show === true ? "open" : ""}`}>
                {
                    product?.desc?.length > 0
                        ?   <p>{product.desc}</p>
                        :   <></>
                }
                <div className="feature">
                    <p>Accounts</p>
                    <div>
                    {product.accounts !== -1 ? product.accounts : 'Unlimited'} 
                    </div>
                </div>
                <div className="feature">
                    <p>Users</p>
                    <div>
                        {product.users !== -1 ? product.users : "Unlimited"}
                    </div>
                </div>
                <div className="feature">
                    <p>Integrations</p>
                    <div>{product.integrations ? product.integrations : "None"}</div>
                </div>
                <div className="feature">
                    <p>Data History</p>
                    <div>
                        {product.data_history !== -1 ? product.data_history : 'Unlimited'} {product.data_history !== -1 && (product.data_history > 1 ? 'years' : 'year')}
                    </div>
                </div>
                <div className="feature">
                    <p>Priority Support</p>
                    <div>
                        {product.priority_support === true ? <CheckMark /> : <XMark/>}
                    </div>
                </div>
                <div className="feature">
                    <p>Email Support</p>
                    <div>
                        {product.email_support === true ? <CheckMark /> : <XMark/>}
                    </div>
                </div>
                <div className="feature">
                    <p>Onboarding</p>
                    <div>
                        {product.onboarding === true ? <CheckMark /> : <XMark/>}
                    </div>
                </div>
                <div className="feature">
                    <p>Custom Reports</p>
                    <div>
                        {product.custom_reports === true ? <CheckMark /> : <XMark/>}
                    </div>
                </div>
            </div>
        </>
    )
}

const ProductCard: React.FC<IProductCardProps> = ({ product, toggle, isShopifyFlow = false, shopDomain = null, switchedStore = false, promoCode = '', promo = null }) => {
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  // Shopify-billed plan clicked outside the Shopify flow (no ?shop= params) —
  // block the purchase and point the merchant at the Shopify App Store.
  const [showShopifyRequiredModal, setShowShopifyRequiredModal] = useState(false)

  const { currentOrganization } = useContext(OrganizationsContext)
  const history = useHistory()
  const token = useSelector(selectToken)

  const { loadingCreatingConversation } = useConversation(currentOrganization.id)

  const handleShopifyCheckout = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const response = await api.createShopifySubscription({
        organizationId: currentOrganization.id,
        token,
        productId: product.id,
        annual: toggle,
        shop: shopDomain || undefined,
        switched: switchedStore || undefined,
        promoCode: promoCode.trim() || undefined
      })

      if (response.success && response.confirmation_url) {
        // New or resumed subscription -> send them to Shopify to approve the charge.
        window.location.href = response.confirmation_url
      } else if (response.success && response.already_subscribed) {
        // Genuinely subscribed to this exact plan already -> manage it, don't error.
        history.push(`/organization/${currentOrganization.slug}/settings/subscriptions`)
      } else {
        setError('We couldn’t start your subscription. Contact support if this keeps happening.')
      }
    } catch (err: any) {
      console.error('Shopify subscription creation failed:', err)
      const detail = err?.response?.data?.detail
      // No usable shop context for this charge (store never OAuth'd, no connection, a dead
      // token — e.g. the app was uninstalled, revoking it — or a different store holds the
      // live subscription): route into the Shopify connect flow — it re-runs OAuth for the
      // named store (re-minting the token) and, when another store is active, lands on the
      // store-switch confirmation instead of dead-ending here with a raw error.
      if (['shop_not_connected', 'no_shopify_connection', 'switch_required', 'shop_token_invalid'].includes(detail?.type)) {
        const shopParam = detail?.shop || shopDomain
        history.push(`/shopify-integration${shopParam ? `?shop=${encodeURIComponent(shopParam)}` : ''}`)
        return
      }
      setError(detail?.message || 'We couldn’t start your subscription. Contact support if this keeps happening.')
    } finally {
      setLoading(false)
    }
  }

  const handleClick = (e) => {
    e.preventDefault()

    if (!product.can_purchase) {
      return;
    }

    // Custom "talk to us" plans (no fixed price) -> open the in-app HubSpot chat
    // (the "Maya" widget, loaded via GTM). Checked BEFORE isShopifyFlow so an Enterprise
    // card inside the Shopify flow opens the chat instead of attempting a charge.
    if (product.has_custom_price && product.annual_price === 0) {
      const hs = (window as any).HubSpotConversations
      if (hs?.widget?.open) {
        hs.widget.open()
      } else {
        console.warn('HubSpot chat widget not available')
      }
      return
    }

    // shopify users go through different flow
    if (isShopifyFlow) {
      handleShopifyCheckout(e)
      return
    }

    // Shopify-billed plans can never go through Stripe (App Store requirement),
    // and without the ?shopify=true&shop= params we don't know which store to
    // charge — block the purchase instead of silently creating a Stripe session.
    if (product.billing_platform === 'shopify') {
      setShowShopifyRequiredModal(true)
      return
    }

    const url = `/organization/${currentOrganization.slug}/billing/checkout?product=${product.id}&annual=${toggle}`
    history.push(url)
  }

  const formatPrice = (amount: number) => {
    const formatted = amount
      .toLocaleString('en-US', { style: 'currency', currency: 'EUR' })
      .slice(1)
    if (formatted.slice(-3) === '.00') {
      return formatted.slice(0, -3)
    }
    return formatted
  }

  const handlePrice = () => {
    if (product.mode === ProductMode.SUBSCRIPTION) {
      if (product.annual_price !== null) {
        if (product.has_custom_price === true && product.annual_price !== 0) {
          return formatPrice(parseInt(product.annual_price?.toLocaleString()) / 12)
        } else if (product.has_custom_price === true) {
          return 'Custom'
        }
    
        // Show the real per-interval price: the annual price for the annual toggle, the
        // monthly price otherwise. (Previously annual was monthly×12, which both
        // mispriced the card and didn't match what Shopify was actually charged.)
        // Guard nulls so we never render "€NaN" if a price is missing for an interval.
        const amount = toggle ? product.annual_price : product.monthly_price
        if (amount === null || amount === undefined) return ''
        return formatPrice(amount)
      }
    } else {
      if (product.fixed_price) 
      return formatPrice(product.fixed_price)
    }
  }

  // "€0 for your first 3 months" under the list price, for a fixed-price subscription.
  const promoPriceNote = () => {
    if (!promo || !isShopifyFlow || product.mode !== ProductMode.SUBSCRIPTION || product.has_custom_price) {
      return null
    }
    const amount = toggle ? product.annual_price : product.monthly_price
    if (amount === null || amount === undefined) return null
    const discounted = formatPrice((amount * (100 - promo.percentage)) / 100)
    const unit = promo.billing_interval === 'ANNUAL' ? 'year' : 'month'
    if (!promo.duration_intervals) return `€${discounted} with ${promo.code}`
    if (promo.duration_intervals === 1) return `€${discounted} for your first ${unit}`
    return `€${discounted} for your first ${promo.duration_intervals} ${unit}s`
  }

  const handleButton = () => {
    if (loading || loadingCreatingConversation === true) {
      return <ScreenLoading spinnerWidth="sm" spinnerColor="white" />
    }

    if (!product.can_purchase) {
      return "Purchased"
    }

    if (product.annual_price === 0) {
      return 'Contact us'
    }

    return 'Buy ' + product.title
  }

  return (
    <>
      <div className="product-card" data-featured={product.is_most_popular}>
        <div className="product-header">
          <h1 className="product-title">{product.title}</h1>
          <div
            className={`product-price ${product.mode === ProductMode.PAYMENT || (product.has_custom_price && product.annual_price) === 0 ? 'custom' : ''} ${product.mode === ProductMode.SUBSCRIPTION ? (toggle ? 'annually' : 'monthly') : "custom"} ${product.has_custom_price && product.annual_price === 0 ? 'is-custom-text' : ''}`}
          >
            {handlePrice()}
          </div>
          {product.has_custom_price === false && (
            <p className="product-reccurring">
              {product.mode === ProductMode.SUBSCRIPTION ? "Billed " + (toggle ? 'annually' : 'monthly') : "Billed once"}
            </p>
          )}
          {promoPriceNote() && <p className="product-promo">{promoPriceNote()}</p>}
        </div>
        <div className="product-body">
          <Features product={product} show={true} />

          {error && (
            <div style={{
              padding: '8px',
              marginBottom: '12px',
              backgroundColor: '#fee',
              color: '#c00',
              borderRadius: '4px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <div className="button-wrapper">
            <div className={`product-button ${!product.can_purchase ? 'disabled' : ''}`}>
              <button onClick={handleClick}>{handleButton()}</button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        className="shopify-required-modal"
        show={showShopifyRequiredModal}
        onHide={() => setShowShopifyRequiredModal(false)}
        styles={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          maxWidth: "480px",
          padding: "32px",
          textAlign: "center"
        }}
      >
        <h2>Shop details required</h2>
        <p>
          This plan is billed through Shopify, so we need to know which store to charge.
          Install Maya Insights from the Shopify App Store and pick your store — you’ll come
          straight back here to choose a plan.
        </p>
        {/* The listing is the only entry point that gives us a shop domain + OAuth grant.
            A plain link (not window.location) so the merchant chooses when to leave. */}
        <a
          className="submit-button"
          style={{ alignSelf: "center", marginTop: 0 }}
          href={SHOPIFY_APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open the Shopify App Store
        </a>
      </Modal>
    </>
  )
}

export default ProductCard
